import { ChangeEvent, FormEvent, useState } from 'react';
import { admin, TripRules, VehicleDetail as Data } from '../api';
import { ApplyFrom, fromLabel, toIso } from './InputMapping';

// Journey rules, as a box on the unit page. Admins edit; everyone else sees what is set.
// Changes recalculate journeys in the background.
export function JourneyRules({ data, canEdit, onSaved }: { data: Data; canEdit: boolean; onSaved: (msg: string) => void }) {
  return <Rules data={data} canEdit={canEdit} onSaved={() => onSaved('Journey rules saved; journeys recalculated.')} />;
}

function Rules({ data, canEdit, onSaved }: { data: Data; canEdit: boolean; onSaved: () => void }) {
  const current = data.settings[0];
  const [r, setR] = useState<TripRules>(current ?? {
    validFrom: null, method: 'movement', minStopS: 300, minTripM: 300, minTripS: 60, maxGapS: 1200,
    startSpeedKmh: 5, stopRadiusM: 100, idleS: 180,
  });
  const [from, setFrom] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const min = (s: number) => Math.round((s / 60) * 10) / 10;
  const num = (field: keyof TripRules, factor = 1) => ({
    value: (r[field] as number) / factor,
    onChange: (e: ChangeEvent<HTMLInputElement>) => setR({ ...r, [field]: Math.round(Number(e.target.value) * factor) }),
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await admin.saveRules(data.vehicle.id, { ...r, validFrom: toIso(from) });
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    }
    setBusy(false);
  };

  return (
    <form className="card form rules" onSubmit={submit} aria-label="Journey rules">
      <h2>Journey rules</h2>
      {!canEdit && <p className="muted small">Only admins can change these.</p>}
      <fieldset className="form" disabled={!canEdit}>
      <fieldset className="choice">
        <legend className="sr-only">How journeys are decided</legend>
        <label className={`option${r.method === 'ignition' ? ' on' : ''}`}>
          <input type="radio" name="method" checked={r.method === 'ignition'} onChange={() => setR({ ...r, method: 'ignition' })} />
          <span><b>Ignition</b><br /><span className="muted small">From ignition on to off. Needs an Ignition input.</span></span>
        </label>
        <label className={`option${r.method === 'movement' ? ' on' : ''}`}>
          <input type="radio" name="method" checked={r.method === 'movement'} onChange={() => setR({ ...r, method: 'movement' })} />
          <span><b>Movement</b><br /><span className="muted small">While it moves. For trailers and units without ignition.</span></span>
        </label>
      </fieldset>
      <div className="grid3">
        <label>Minimum stop (min)<input id="r-stop" type="number" min={0.5} step={0.5} required {...num('minStopS', 60)} /></label>
        <label>Minimum journey (m)<input id="r-tripm" type="number" min={0} step={50} required {...num('minTripM')} /></label>
        <label>Minimum journey (min)<input id="r-trips" type="number" min={0} step={0.5} required {...num('minTripS', 60)} /></label>
        <label>Signal lost after (min)<input id="r-gap" type="number" min={1} step={1} required {...num('maxGapS', 60)} /></label>
        {r.method === 'ignition' ? (
          <label>Idle after (min)<input id="r-idle" type="number" min={0.5} step={0.5} required {...num('idleS', 60)} /></label>
        ) : (
          <>
            <label>Start speed (km/h)<input id="r-speed" type="number" min={1} max={50} step={1} required {...num('startSpeedKmh')} /></label>
            <label>Stop area radius (m)<input id="r-radius" type="number" min={10} max={2000} step={10} required {...num('stopRadiusM')} /></label>
          </>
        )}
      </div>
      </fieldset>
      {canEdit && (
        <>
          <ApplyFrom id="rules-from" value={from} onChange={setFrom} />
          {error && <p className="error" role="alert">{error}</p>}
          <div className="actions"><button className="primary" disabled={busy}>Save rules</button></div>
        </>
      )}
      {data.settings.length > 0 && (
        <details>
          <summary className="small">Rule history ({data.settings.length})</summary>
          <ul className="small">
            {data.settings.map((s) => (
              <li key={s.validFrom ?? 'start'}>{fromLabel(s.validFrom)}: {s.method}, stop {min(s.minStopS)} min, journey ≥ {s.minTripM} m</li>
            ))}
          </ul>
        </details>
      )}
    </form>
  );
}
